import type { ConnectorStatus } from '../../../../lib/connectors';
import { type AppsController } from '../../../../lib/use-connectors';
import { useTranslation } from '../../../../lib/i18n';
import { ConnectedConnectorCard } from './MobileConnectorRow';
import { CardStack } from './CardStack';

/**
 * "Connected · N" (Figma 13131-79009): the connected tools as an aggregated deck that flattens on
 * "View All". The deck itself is the shared `CardStack`; this component just supplies the connected
 * cards and the section's title/count.
 */
export function ConnectedStack({
  connected,
  controller,
  target,
}: {
  connected: ConnectorStatus[];
  controller: AppsController;
  target?: string;
}) {
  const { t } = useTranslation();
  const { busy, disconnect, addAnother, connect } = controller;
  const multiAccountEnabled = controller.state.kind === 'ready' && controller.state.multiAccountEnabled;

  if (connected.length === 0) return null;

  return (
    <section>
      <CardStack
        key={target}
        initiallyExpanded={target === 'shopify-direct' && connected.some((connector) => connector.toolkit === target)}
        title={t('connectors.apps.connected')}
        count={connected.length}
        biToggle="connected"
        items={connected.map((connector) => ({
          key: connector.toolkit,
          // Collapsed in the fan the card is a compact header peek; "View All" flattens it to the full
          // per-account card (Figma 13738-149971).
          node: ({ peek }: { peek: boolean }) => (
            <ConnectedConnectorCard
              connector={connector}
              busy={busy}
              multiAccountEnabled={multiAccountEnabled}
              compact={peek}
              onDisconnect={disconnect}
              onAddAnother={addAnother}
              onConnect={connect}
            />
          ),
        }))}
      />
    </section>
  );
}
